// Appareils : demande d'approbation, et approbation depuis l'appareil principal.
//
// Un appareil inconnu ne s'enrôle pas tout seul. Il **demande**, et reste sans
// aucun droit tant qu'il n'est pas approuvé : il ne peut ni écrire son état, ni
// publier de KeyPackage — donc personne ne peut l'ajouter à un dossier.
//
// ⚠️ L'empreinte doit être comparée **à l'œil**, sur les deux écrans. C'est le
// seul rempart : le serveur, lui, ne peut pas distinguer l'appareil du
// propriétaire de celui d'un intrus qui aurait volé son mot de passe.

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import {
  listDevices,
  openDeviceSession,
  reclaimDevice,
  type DeviceSummary,
} from '../crypto/mls-state';
import { useAuth } from './store';

/** Empreinte lisible : 8 octets suffisent à comparer d'un coup d'œil. */
function empreinte(bytes: Uint8Array): string {
  return Array.from(bytes.slice(0, 8))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ');
}

const fromBase64 = (b64: string) =>
  Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

// ---------------------------------------------------------------------------

/** Écran de l'appareil qui attend d'être approuvé. */
export function PendingApproval({
  tenantSlug,
  email,
}: {
  tenantSlug: string;
  email: string;
}) {
  const session = useAuth((s) => s.session);
  const me = useAuth((s) => s.me);
  const masterKey = useAuth((s) => s.masterKey);
  const pending = useAuth((s) => s.pendingDevice);
  const setDevice = useAuth((s) => s.setDevice);
  const [error, setError] = useState<string | null>(null);

  // Les appareils déjà actifs du compte : ce sont eux qu'on peut reprendre si le
  // principal est perdu.
  const [existing, setExisting] = useState<DeviceSummary[]>([]);
  const [perdu, setPerdu] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  // On guette l'approbation : dès qu'elle tombe, l'appareil devient
  // pleinement opérationnel sans que l'utilisateur ait à se reconnecter.
  useEffect(() => {
    if (!session || !me || !masterKey) return;
    let stop = false;

    const tick = async () => {
      try {
        const outcome = await openDeviceSession(
          session.sessionToken,
          masterKey.masterKey,
          tenantSlug,
          email,
          me.userId,
        );
        if (!stop && outcome.kind === 'ready') setDevice(outcome.device);
      } catch (e) {
        if (!stop) setError(message(e));
      }
    };

    const id = setInterval(() => void tick(), 4000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [session, me, masterKey, tenantSlug, email, setDevice]);

  useEffect(() => {
    if (!session) return;
    void listDevices(session.sessionToken)
      .then((all) => setExisting(all.filter((d) => d.status === 'active')))
      .catch((e) => setError(message(e)));
  }, [session]);

  if (!pending) return null;

  /** Redevenir un appareil qu'on a perdu — cf. `reclaimDevice`. */
  async function reprendre(target: DeviceSummary) {
    setBusy(target.id);
    setError(null);
    try {
      setDevice(
        await reclaimDevice(
          session!.sessionToken,
          masterKey!.masterKey,
          tenantSlug,
          email,
          target.id,
          pending!.deviceId,
        ),
      );
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      className="mx-auto mt-10 max-w-md rounded border border-amber-200 bg-amber-50 p-4 text-left"
      data-testid="pending-approval"
    >
      <h2 className="text-sm font-semibold text-amber-900">
        Cet appareil attend ton approbation
      </h2>
      <p className="mt-2 text-sm text-amber-800">
        Ouvre Delo sur ton appareil principal, puis approuve celui-ci — après
        avoir vérifié que l&apos;empreinte ci-dessous est bien{' '}
        <strong>identique</strong> à celle qui s&apos;y affiche.
      </p>

      <p
        data-testid="pending-fingerprint"
        className="mt-3 rounded bg-white px-3 py-2 text-center font-mono text-sm tracking-widest text-slate-800"
      >
        {empreinte(pending.signaturePublicKey)}
      </p>

      <p className="mt-3 text-xs text-amber-700">
        Si elle diffère, <strong>n&apos;approuve pas</strong> : quelqu&apos;un
        d&apos;autre tente d&apos;entrer avec ton mot de passe.
      </p>

      {/* L'impasse : celui qui a récupéré son compte APRÈS avoir perdu son
          appareil attendrait ici une approbation que personne ne peut donner —
          son appareil principal, seul habilité, n'existe plus. */}
      {existing.length > 0 && (
        <div className="mt-5 border-t border-amber-200 pt-4">
          {!perdu ? (
            <button
              type="button"
              onClick={() => setPerdu(true)}
              data-testid="lost-device"
              className="w-full text-xs text-amber-800 underline hover:text-amber-900"
            >
              Je n&apos;ai plus mon appareil principal (perdu, volé, remplacé)
            </button>
          ) : (
            <div data-testid="reclaim-panel">
              <p className="text-xs text-amber-800">
                Alors ne fais pas approuver celui-ci : <strong>reprends</strong>{' '}
                l&apos;appareil perdu. Tu retrouveras sa place dans tes dossiers et
                tout ton historique — un appareil neuf, lui, repartirait de rien.
              </p>
              <ul className="mt-3 space-y-2">
                {existing.map((d) => (
                  <li key={d.id} className="rounded bg-white p-3">
                    <p className="text-xs text-slate-500">
                      {d.isPrimary ? 'Appareil principal' : 'Appareil secondaire'} —
                      créé le {new Date(d.createdAt).toLocaleDateString('fr-FR')}
                    </p>
                    <p className="mt-1 font-mono text-xs tracking-widest text-slate-800">
                      {empreinte(fromBase64(d.signaturePublicKey))}
                    </p>
                    <button
                      type="button"
                      onClick={() => reprendre(d)}
                      disabled={busy === d.id}
                      data-testid="reclaim-device"
                      className="mt-2 w-full rounded bg-blue-900 px-3 py-2 text-xs font-medium text-white hover:bg-blue-800 disabled:opacity-50"
                    >
                      {busy === d.id ? 'Reprise…' : 'Reprendre cet appareil'}
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-[11px] text-amber-700">
                Tes autres sessions seront fermées : si quelqu&apos;un détient
                l&apos;appareil perdu, il perdra la main.
              </p>
            </div>
          )}
        </div>
      )}

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </section>
  );
}

// ---------------------------------------------------------------------------

/** Panneau de l'appareil principal : approuver les demandes. */
export function DeviceApprovals() {
  const session = useAuth((s) => s.session);
  const device = useAuth((s) => s.device);

  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!session) return;
    try {
      setDevices(await listDevices(session.sessionToken));
    } catch (e) {
      setError(message(e));
    }
  }, [session]);

  // Une demande peut arriver à tout moment : on regarde régulièrement.
  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 6000);
    return () => clearInterval(id);
  }, [refresh]);

  // Seul l'appareil principal peut approuver.
  if (!session || !device?.isPrimary) return null;

  const pending = devices.filter((d) => d.status === 'pending');
  if (pending.length === 0) return null;

  async function approve(id: string) {
    setBusy(id);
    setError(null);
    try {
      await api(`/devices/${id}/approve`, {
        method: 'POST',
        token: session!.sessionToken,
      });
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  }

  async function refuse(id: string) {
    setBusy(id);
    setError(null);
    try {
      await api(`/devices/${id}`, {
        method: 'DELETE',
        token: session!.sessionToken,
      });
      await refresh();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      className="mt-6 rounded border border-amber-200 bg-amber-50 p-4 text-left"
      data-testid="device-approvals"
    >
      <h3 className="text-sm font-semibold text-amber-900">
        {pending.length === 1
          ? 'Un appareil demande à rejoindre ton compte'
          : `${pending.length} appareils demandent à rejoindre ton compte`}
      </h3>
      <p className="mt-1 text-xs text-amber-800">
        N&apos;approuve que si l&apos;empreinte est <strong>exactement</strong>{' '}
        celle affichée sur l&apos;autre appareil.
      </p>

      <ul className="mt-3 space-y-3">
        {pending.map((d) => (
          <li key={d.id} className="rounded bg-white p-3">
            <p
              data-testid="approval-fingerprint"
              className="text-center font-mono text-sm tracking-widest text-slate-800"
            >
              {empreinte(fromBase64(d.signaturePublicKey))}
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => approve(d.id)}
                disabled={busy === d.id}
                data-testid="approve-device"
                className="flex-1 rounded bg-blue-900 px-3 py-2 text-xs font-medium text-white hover:bg-blue-800 disabled:opacity-50"
              >
                {busy === d.id ? '…' : 'Approuver'}
              </button>
              <button
                type="button"
                onClick={() => refuse(d.id)}
                disabled={busy === d.id}
                data-testid="refuse-device"
                className="rounded border border-slate-300 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Refuser
              </button>
            </div>
          </li>
        ))}
      </ul>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </section>
  );
}
